import { Spinner } from '@app/components/common/SpinSlump/SpinSlump.styles';
import { FC, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as S from './styles';
import { Col, DatePicker, Divider, Row } from 'antd';
import { setHeader } from '@app/store/slices/headerSlice';
import { useDispatch } from 'react-redux';
import { IconFileExcel, IconFilePdf } from '@app/assets/slump-icons';
import { WaterPieChart } from '@app/pages/modules/CentralTracking/components/WaterPieChart/WaterPieChart';
import { TableReportCentralAnalytic } from './components/Table/TableReportCentralAnalytic';
import moment from 'moment';
import {
  exportCentralAnalyticReportToExcel,
  exportCentralAnalyticReportToPdf,
} from './functions/central-analytic-functions';
import { readUser } from '@app/services/localStorage.service';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { BaseFormInputItem } from '@app/components/common/forms/components/BaseFormInputItem/BaseFormInputItem';
import { Select } from '@app/components/common/selects/Select/Select';
import { GroupModel } from '@app/domain/interfaces/client_branch/groupModel';
import { ClientBranchModel } from '@app/domain/interfaces/client_branch/clientBranchModel';
import IGroupService, { GroupService } from '@app/services/groupService';
import IClientBranchService, { ClientBranchService } from '@app/services/clientBranchService';
import { notificationController } from '@app/controllers/notificationController';
import { Button } from '@app/components/common/buttons/Button/Button';
import { SearchIcon } from '@app/components/header/components/HeaderSearch/HeaderSearch.styles';
import { ReportFilterModel } from '@app/domain/interfaces/report/reportFilterModel';
import { ReportCentralAnalyticModel } from '@app/domain/interfaces/report/reportCentralAnalyticModel';
import IReportService, { ReportService } from '@app/services/reportService';
import { ClearOutlined } from '@ant-design/icons';
import { getDifferenceDaysBetweenDates } from '@app/utils/utils';
import { WaterChartCentralAnalytic } from './components/Charts/WaterChartCentralAnalytic';
const { RangePicker } = DatePicker;

const groupService: IGroupService = new GroupService();
const clientBranchService: IClientBranchService = new ClientBranchService();
const reportService: IReportService = new ReportService();

const filters = [
  'Todas viagens',
  'Água central menor ou igual 30% da água retida',
  'Água central entre 30%-70% da água retida',
  'Água central maior ou igual que 70% da água retida ',
  'Outros eventos',
];

const periods = [
  {
    value: 1,
    label: 'Últimos 7 dias',
  },
  {
    value: 2,
    label: 'Mês atual',
  },
  {
    value: 3,
    label: 'Mês passado',
  },
  {
    value: 4,
    label: 'Personalizar',
  },
];

export const CentralAnalyticReport: FC = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [filterSelected, setFilterSelected] = useState<number>(0);
  const [normalWaterQuantity, setNormalWaterQuantity] = useState<number>(0);
  const [warningWaterQuantity, setWarningWaterQuantity] = useState<number>(0);
  const [dangerWaterQuantity, setDangerWaterQuantity] = useState<number>(0);
  const [errorWaterQuantity, setErrorWaterQuantity] = useState<number>(0);
  const [reportData, setReportData] = useState<ReportCentralAnalyticModel>();
  const [reportDataFiltered, setReportFilteredData] = useState<ReportCentralAnalyticModel>();
  const [groups, setGroups] = useState<GroupModel[]>([]);
  const [branches, setBranches] = useState<ClientBranchModel[]>([]);
  const [chargingPoints, setChargingPoints] = useState<{ id: number; nome: string }[]>([]);
  const [period, setPeriod] = useState<number>();
  const [showCustomPeriod, setShowCustomPeriod] = useState(false);
  const [reportFilter, setReportFilter] = useState<ReportFilterModel>({} as ReportFilterModel);
  const [customRangeDate, setCustomRangeDate] = useState<(moment.Moment | null)[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const [selectedRegional, setSelectedRegional] = useState<number | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<number | null>(null);
  const [selectedChargingPoint, setSelectedChargingPoint] = useState<number | null>(null);

  const fetch = useCallback(async () => {
    if (selectedBranch && reportFilter.dataHoraInicio && reportFilter.dataHoraFim) {
      if (getDifferenceDaysBetweenDates(reportFilter.dataHoraInicio, reportFilter.dataHoraFim) > 31) {
        notificationController.warning({ message: 'O período máximo para emissão do relatório é de 31 dias' });
        return;
      }

      setLoading(true);
      reportService
        .getCentralAnalyticReport({
          dataHoraInicio: reportFilter.dataHoraInicio ?? new Date(),
          dataHoraFim: reportFilter.dataHoraFim ?? new Date(),
          codigoFilialSiac: selectedBranch,
          codigoCentroCustoSiac: selectedChargingPoint ?? undefined,
        })
        .then((res) => {
          setLoading(false);
          setReportData(res);
        })
        .catch(() => {
          setLoading(false);
          notificationController.error({ message: 'Erro ao buscar os dados do relatório' });
        });
    } else notificationController.error({ message: 'Selecione uma filial (ou um ponto de carga) e um período' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, selectedChargingPoint, reportFilter]);

  const fetchGroups = useCallback(async () => {
    setGroups(await groupService.getArray(''));
  }, []);

  const fetchBranchesByGroup = useCallback(async (idAgrupamento: number) => {
    setBranches(await clientBranchService.getArray(`/obterPorAgrupamento/${idAgrupamento}`));
  }, []);

  const fetchBranchesByRegional = useCallback(async (idRegional: number) => {
    const response = await clientBranchService.getArray(`/obterPorRegional/${idRegional}`);

    if (response.length > 0) {
      setBranches(response);
    } else {
      if (selectedGroup) {
        fetchBranchesByGroup(selectedGroup);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterButtonClick = (indexFilterClicked: number) => {
    setFilterSelected(indexFilterClicked);

    switch (indexFilterClicked) {
      case 0:
        setReportFilteredData(reportData);
        break;
      case 1:
        setReportFilteredData({
          ...reportData,
          obras:
            reportData?.obras
              ?.map((o) => ({
                ...o,
                remessas: o.remessas.filter((r) => !r.finalizadoComErro && (r.aguaUtilizadaPorcentagem ?? 0) <= 30),
              }))
              .filter((o) => o.remessas.length > 0) ?? [],
        });
        break;
      case 2:
        setReportFilteredData({
          ...reportData,
          obras:
            reportData?.obras
              ?.map((o) => ({
                ...o,
                remessas: o.remessas.filter(
                  (r) =>
                    !r.finalizadoComErro &&
                    (r.aguaUtilizadaPorcentagem ?? 0) > 30 &&
                    (r.aguaUtilizadaPorcentagem ?? 0) < 70,
                ),
              }))
              .filter((o) => o.remessas.length > 0) ?? [],
        });
        break;
      case 3:
        setReportFilteredData({
          ...reportData,
          obras:
            reportData?.obras
              ?.map((o) => ({
                ...o,
                remessas: o.remessas.filter((r) => !r.finalizadoComErro && (r.aguaUtilizadaPorcentagem ?? 0) > 70),
              }))
              .filter((o) => o.remessas.length > 0) ?? [],
        });
        break;
      case 4:
        setReportFilteredData({
          ...reportData,
          obras:
            reportData?.obras
              ?.map((o) => ({ ...o, remessas: o.remessas.filter((r) => r.finalizadoComErro) }))
              .filter((o) => o.remessas.length > 0) ?? [],
        });
        break;

      default:
        break;
    }
  };

  const handlePeriodChange = (id: number) => {
    setPeriod(id);
    setShowCustomPeriod(id === 4);

    switch (id) {
      case 1:
        setReportFilter({
          ...reportFilter,
          dataHoraFim: new Date(),
          dataHoraInicio: moment().utc().add(-7, 'd').toDate(),
        });
        break;
      case 2:
        setReportFilter({
          ...reportFilter,
          dataHoraFim: moment().utc().endOf('month').toDate(),
          dataHoraInicio: moment().utc().startOf('month').toDate(),
        });
        break;
      case 3:
        setReportFilter({
          ...reportFilter,
          dataHoraFim: moment().utc().add(-1, 'M').endOf('month').toDate(),
          dataHoraInicio: moment().utc().add(-1, 'M').startOf('month').toDate(),
        });
        break;
      default:
        break;
    }
  };

  const handleChangeRangePicker = (dates: [moment.Moment, moment.Moment]) => {
    setCustomRangeDate(dates);
    setReportFilter({
      ...reportFilter,
      dataHoraInicio: dates[0].hours(12).utc().toDate(),
      dataHoraFim: dates[1].hours(12).utc().toDate(),
    });
  };

  const handleExportPdf = () => {
    if (reportDataFiltered)
      exportCentralAnalyticReportToPdf(
        {
          regional: searchParams.get('regional') ?? '-',
          filial: searchParams.get('filial') ?? '-',
          dataHora: moment(new Date()).format('DD/MM/yyyy'),
          usuario: readUser()?.username ?? '-',
          pontoCarga: selectedBranch?.toString() ?? '-',
        },
        reportDataFiltered,
      );
  };

  const handleExportExcel = () => {
    if (reportDataFiltered)
      exportCentralAnalyticReportToExcel(
        {
          regional: searchParams.get('regional') ?? '-',
          filial: searchParams.get('filial') ?? '-',
          dataHora: moment(new Date()).format('DD/MM/yyyy'),
          usuario: readUser()?.username ?? '-',
          pontoCarga: selectedBranch?.toString() ?? '-',
        },
        reportDataFiltered,
      );
  };

  useEffect(() => {
    setReportFilteredData(reportData);
  }, [reportData]);

  useEffect(() => {
    let normalCount = 0;
    let warningCount = 0;
    let dangerCount = 0;
    let errorCount = 0;

    reportDataFiltered?.obras?.map((o) => {
      errorCount += o.remessas.filter((r) => r.finalizadoComErro)?.length ?? 0;
      normalCount +=
        o.remessas.filter((r) => !r.finalizadoComErro && (r.aguaUtilizadaPorcentagem ?? 0) <= 30)?.length ?? 0;
      warningCount +=
        o.remessas.filter(
          (r) =>
            !r.finalizadoComErro && (r.aguaUtilizadaPorcentagem ?? 0) > 30 && (r.aguaUtilizadaPorcentagem ?? 0) < 70,
        )?.length ?? 0;
      dangerCount +=
        o.remessas.filter((r) => !r.finalizadoComErro && (r.aguaUtilizadaPorcentagem ?? 0) > 70)?.length ?? 0;
    });

    setNormalWaterQuantity(normalCount);
    setWarningWaterQuantity(warningCount);
    setDangerWaterQuantity(dangerCount);
    setErrorWaterQuantity(errorCount);
  }, [reportDataFiltered]);

  useEffect(() => {
    dispatch(
      setHeader({
        title: 'Relatório analítico da central',
      }),
    );

    fetchGroups();
  }, [dispatch, fetchGroups]);

  useEffect(() => {
    if (selectedGroup) {
      fetchBranchesByGroup(selectedGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  useEffect(() => {
    if (selectedRegional) {
      fetchBranchesByRegional(selectedRegional);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegional]);

  useEffect(() => {
    if (branches.length > 0 && selectedBranch) {
      const newChargingPoints: { id: number; nome: string }[] = [];

      branches
        .filter((branch) => branch.codigoFilialSiac === selectedBranch)
        .map((branch) => {
          if (branch.codigoPontoDeCarga01) {
            newChargingPoints.push({ id: branch.codigoPontoDeCarga01, nome: 'P1' });
          }

          if (branch.codigoPontoDeCarga02) {
            newChargingPoints.push({ id: branch.codigoPontoDeCarga02, nome: 'P2' });
          }

          if (branch.codigoPontoDeCarga03) {
            newChargingPoints.push({ id: branch.codigoPontoDeCarga03, nome: 'P3' });
          }
        });

      setChargingPoints(newChargingPoints);
    }
  }, [branches, selectedBranch]);

  return (
    <Spinner spinning={loading}>
      <div style={{ marginLeft: '2rem', marginTop: '1rem', padding: '1rem' }}>
        <BaseForm
          style={{
            width: '100%',
          }}
        >
          <Row gutter={12}>
            <Col xs={24} md={4}>
              <BaseFormInputItem style={{ width: '100%' }} label="Grande centro">
                <Select
                  placeholder="Selecione o grande centro"
                  options={groups.map((g) => ({ label: g.nome, value: g.id }))}
                  value={selectedGroup}
                  onChange={(e) => {
                    setSelectedGroup(e as unknown as number);
                    setSelectedRegional(null);
                    setSelectedBranch(null);
                  }}
                />
              </BaseFormInputItem>
            </Col>

            <Col xs={24} md={4}>
              <BaseFormInputItem style={{ width: '100%' }} label="Filial">
                <Select
                  placeholder="Selecione a filial"
                  options={branches.map((b) => ({ label: b.nome, value: b.codigoFilialSiac }))}
                  disabled={!selectedGroup}
                  onChange={(e) => setSelectedBranch(e as unknown as number)}
                  value={selectedBranch}
                />
              </BaseFormInputItem>
            </Col>

            <Col xs={24} md={4}>
              <BaseFormInputItem style={{ width: '100%' }} label="Ponto de carga">
                <Select
                  placeholder="Selecione o ponto de carga"
                  options={chargingPoints.map((cp) => ({ label: cp.nome, value: cp.id }))}
                  disabled={!selectedBranch}
                  onChange={(e) => setSelectedChargingPoint(e as unknown as number)}
                  value={selectedChargingPoint}
                />
              </BaseFormInputItem>
            </Col>

            <Col xs={24} md={4}>
              <BaseFormInputItem label="Tipo do período">
                <Select
                  disabled={!selectedBranch}
                  value={period}
                  options={periods}
                  onChange={(value) => handlePeriodChange(value as number)}
                  placeholder="Selecione o tipo do perído"
                />
              </BaseFormInputItem>
            </Col>

            {showCustomPeriod && (
              <Col xs={24} md={4}>
                <BaseFormInputItem label="Períodos">
                  <RangePicker
                    value={[customRangeDate[0], customRangeDate[1]]}
                    onChange={(values) => handleChangeRangePicker(values as [moment.Moment, moment.Moment])}
                    format={'DD-MM-YYYY'}
                    style={{ height: '3.375rem' }}
                    clearIcon={false}
                  />
                </BaseFormInputItem>
              </Col>
            )}

            <Col xs={24} md={2} style={{ display: 'flex', alignItems: 'end' }}>
              <BaseFormInputItem label=" ">
                <Button type="primary" onClick={() => fetch()} disabled={!selectedBranch && !reportFilter}>
                  <span style={{ color: '#FFF', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <SearchIcon />
                  </span>
                </Button>
              </BaseFormInputItem>
              <BaseFormInputItem label=" ">
                <Button
                  type="link"
                  onClick={() => {
                    setReportFilter({} as ReportFilterModel);
                    setSelectedBranch(null);
                    setSelectedChargingPoint(null);
                    setPeriod(undefined);
                    setCustomRangeDate([null, null]);
                    setShowCustomPeriod(false);
                    setSelectedGroup(null);
                  }}
                >
                  <ClearOutlined />
                </Button>
              </BaseFormInputItem>
            </Col>
          </Row>
        </BaseForm>
      </div>

      <Divider type="horizontal" style={{ borderColor: 'var(--disabled-bg-color)' }} />

      {reportData && (
        <div style={{ padding: '0 2rem' }}>
          <Row gutter={[18, 18]} style={{ marginTop: '2rem' }}>
            <Col>
              <S.CardHeaderDescription>Filtro</S.CardHeaderDescription>
            </Col>
          </Row>
          <Row gutter={[8, 8]} style={{ marginTop: '0.5rem' }}>
            {filters.map((description, index) => (
              <Col key={index}>
                <S.FilterButton active={filterSelected == index} onClick={() => handleFilterButtonClick(index)}>
                  {description}
                </S.FilterButton>
              </Col>
            ))}
          </Row>
          <Row justify={'end'} style={{ width: '100%' }}>
            <Col>
              <S.ExportFileButton onClick={() => handleExportPdf()} type="link" icon={<IconFilePdf />}>
                Exportar para pdf
              </S.ExportFileButton>
            </Col>
            <Col>
              <S.ExportFileButton onClick={() => handleExportExcel()} type="link" icon={<IconFileExcel />}>
                Exportar para excel
              </S.ExportFileButton>
            </Col>
          </Row>
          <S.Divider />
          <WaterChartCentralAnalytic
            handleChartFilter={handleFilterButtonClick}
            normalWaterQuantity={normalWaterQuantity}
            warningWaterQuantity={warningWaterQuantity}
            dangerWaterQuantity={dangerWaterQuantity}
            errorWaterQuantity={errorWaterQuantity}
          />
          <TableReportCentralAnalytic reportData={reportDataFiltered} />
        </div>
      )}
    </Spinner>
  );
};
